import { Slot } from '@radix-ui/react-slot';
import { type ComponentProps } from 'react';
import { cn } from '@shared/lib/cn';
import { buttonVariants, type ButtonVariantProps } from './button-variants';
import { Spinner } from './spinner';

export type ButtonProps = ComponentProps<'button'> &
  ButtonVariantProps & {
    asChild?: boolean;
    /**
     * Show an inline spinner and force-disable the button. Keeps the label so
     * the action stays identifiable while the request is in flight. Ignored
     * when `asChild` is set — the consumer owns the rendered element and
     * Radix Slot requires exactly one React-element child.
     */
    loading?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps): React.JSX.Element {
  // `asChild` path: hand the variant classes to a single child via Slot. We
  // must NOT splice an extra <Spinner /> next to the child — Slot uses
  // Children.only and would throw "expected to receive a single React element
  // child" the moment a Spinner / null sibling appears.
  if (asChild) {
    return (
      <Slot className={cn(buttonVariants({ variant, size, className }))} {...props}>
        {children}
      </Slot>
    );
  }
  const isDisabled = (disabled ?? false) || loading;
  return (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={isDisabled}
      aria-busy={loading ? true : undefined}
      {...props}
    >
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}
